import React from 'react';
import { useQueryStates, parseAsString, parseAsInteger } from 'nuqs';
import { useAtom } from 'jotai';
import { activityLogsHydratedAtom, activityLogsListParamsAtom } from '@/store';

export function useActivityLogsFilters() {
  const [params, setParams] = useAtom(activityLogsListParamsAtom);
  const [, setHydrated] = useAtom(activityLogsHydratedAtom);

  const [queryParams, setQueryParams] = useQueryStates({
    page: parseAsInteger.withDefault(1),
    log_name: parseAsString.withDefault('all'),
    event: parseAsString.withDefault('all')
  });

  // Filters state (client UI) mapped to API params (URL synced)
  const [logNameFilter, setLogNameFilter] = React.useState<string>(queryParams.log_name);
  const [eventFilter, setEventFilter] = React.useState<string>(queryParams.event);

  // Sync URL parameters with params atom on mount and when URL changes
  React.useEffect(() => {
    const filters: Record<string, string | undefined> = {};

    // Build filters from URL parameters
    if (queryParams.log_name !== 'all') filters.log_name = queryParams.log_name;
    if (queryParams.event !== 'all') filters.event = queryParams.event;

    // Only update if there's actually a change to avoid unnecessary API calls
    setParams((prev) => {
      const newParams = {
        ...prev,
        page: queryParams.page,
        filters: filters
      };

      // Check if anything actually changed
      if (prev.page === newParams.page &&
        JSON.stringify(prev.filters) === JSON.stringify(newParams.filters)) {
        return prev; // No change, return same object
      }

      return newParams;
    });

    // Mark hydrated after first effective sync
    setHydrated(true);
  }, [queryParams.page, queryParams.log_name, queryParams.event, setParams, setHydrated]);

  // Sync URL parameters with local state on mount
  React.useEffect(() => {
    setLogNameFilter(queryParams.log_name);
    setEventFilter(queryParams.event);
  }, []); // Only run on mount

  const clearFilters = React.useCallback(async () => {
    setLogNameFilter('all');
    setEventFilter('all');
    setParams((prev) => ({ ...prev, page: 1, filters: {} }));
    await setQueryParams({
      page: 1,
      log_name: 'all',
      event: 'all'
    });
  }, [setParams, setQueryParams]);

  const hasAnyFilter = React.useMemo(() => {
    return logNameFilter !== 'all' || eventFilter !== 'all';
  }, [logNameFilter, eventFilter]);

  const handleLogNameChange = React.useCallback((val: string) => {
    setLogNameFilter(val);
    setQueryParams({ page: 1, log_name: val });
    setParams((prev) => ({
      ...prev,
      page: 1,
      filters: { ...prev.filters, log_name: val !== 'all' ? val : undefined }
    }));
  }, [setQueryParams, setParams]);

  const handleEventChange = React.useCallback((val: string) => {
    setEventFilter(val);
    setQueryParams({ page: 1, event: val });
    setParams((prev) => ({
      ...prev,
      page: 1,
      filters: { ...prev.filters, event: val !== 'all' ? val : undefined }
    }));
  }, [setQueryParams, setParams]);

  const handlePageChange = React.useCallback((page: number) => {
    setParams((prev) => ({ ...prev, page }));
    setQueryParams({ page });
  }, [setParams, setQueryParams]);

  return {
    // State
    logNameFilter,
    eventFilter,
    hasAnyFilter,

    // Actions
    clearFilters,
    handleLogNameChange,
    handleEventChange,
    handlePageChange
  };
}

